/**
 * use-notifications — Phase 3b TanStack Query hooks for the notifications
 * bell.
 *
 *   useNotifications()    → useInfiniteQuery on ['notifications'], 20/page.
 *                           Each page is the BE's ListNotificationsPayload
 *                           ({ items, nextCursor, unreadCount }).
 *   useUnreadCount()      → derived from the first page of useNotifications.
 *                           No separate query — every place the bell mounts
 *                           also drives the list query, so this is just a
 *                           thin selector.
 *   useMarkRead()         → PATCH /api/notifications/:id/read.
 *                           Optimistic: stamps `readAt = now()` on the
 *                           matching item and decrements unreadCount across
 *                           all cached pages. Rollback on error.
 *   useMarkAllRead()      → PATCH /api/notifications/read-all.
 *                           Optimistic: stamps `readAt` on every unread
 *                           item and zeroes unreadCount. Rollback on error.
 *
 * Cache shape mirrors `useInfiniteQuery`'s `{ pages: Page[], pageParams }`
 * envelope. Mutations write across every page (the targeted notification
 * may live anywhere in the infinite scroll) and recompute unreadCount on
 * the first page.
 *
 * Notification shape lives in `@/api/types-bridge` until the next codegen
 * run picks up the Phase 3b BE schemas.
 */

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { apiClient } from '@/api/client'
import type { Notification, NotificationsListResponse } from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const

/**
 * Per-tab cache key. The "All" tab uses the canonical
 * `NOTIFICATIONS_QUERY_KEY`; the "Unread" tab adds a discriminator so
 * the two pages don't collide. Realtime invalidations on
 * `NOTIFICATIONS_QUERY_KEY` cascade down to both because the unread
 * tab's key starts with `'notifications'`.
 */
function notificationsKeyFor(unreadOnly: boolean): readonly unknown[] {
  return unreadOnly ? (['notifications', 'unread'] as const) : NOTIFICATIONS_QUERY_KEY
}

const PAGE_SIZE = 20

type NotificationsCache = InfiniteData<NotificationsListResponse, string | null>

/* -------------------------------------------------------------------------- */
/*                              Pagination helpers                            */
/* -------------------------------------------------------------------------- */

function buildListUrl(cursor: string | null, unreadOnly: boolean): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
  if (cursor) params.set('cursor', cursor)
  if (unreadOnly) params.set('unreadOnly', 'true')
  return `/api/notifications?${params.toString()}`
}

/**
 * Apply an in-place mutation across every cached page and recompute the
 * `unreadCount` carried on the first page. The BE is the canonical source
 * of `unreadCount`, but optimistic mutations need to keep the badge in
 * sync until the settle invalidation lands.
 */
function mutateAllPages(
  cache: NotificationsCache | undefined,
  mapItem: (item: Notification) => Notification,
  recomputeUnread: (currentFirstPageUnread: number) => number,
): NotificationsCache | undefined {
  if (!cache || cache.pages.length === 0) return cache
  const first = cache.pages[0]
  if (!first) return cache
  const rest = cache.pages.slice(1)
  return {
    ...cache,
    pages: [
      {
        items: first.items.map(mapItem),
        nextCursor: first.nextCursor,
        unreadCount: Math.max(0, recomputeUnread(first.unreadCount)),
      },
      ...rest.map((page) => ({
        items: page.items.map(mapItem),
        nextCursor: page.nextCursor,
        unreadCount: page.unreadCount,
      })),
    ],
  }
}

/* -------------------------------------------------------------------------- */
/*                              useNotifications                              */
/* -------------------------------------------------------------------------- */

interface UseNotificationsArgs {
  /** Pause the query (e.g. when signed-out). Defaults to true. */
  enabled?: boolean
  /**
   * Filter to only-unread items (server-side `?unreadOnly=true`).
   * Defaults false. Each value gets its own cache page so the All /
   * Unread tabs don't share state.
   */
  unreadOnly?: boolean
}

export function useNotifications({
  enabled = true,
  unreadOnly = false,
}: UseNotificationsArgs = {}) {
  return useInfiniteQuery<
    NotificationsListResponse,
    Error,
    NotificationsCache,
    readonly unknown[],
    string | null
  >({
    queryKey: notificationsKeyFor(unreadOnly),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient<{ data: NotificationsListResponse }>(
        buildListUrl(pageParam ?? null, unreadOnly),
      )
      return res.data
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    staleTime: 15_000,
    // Refetch on window focus — covers the "laptop was closed when a
    // notification fired, opened later" path. Without this, TanStack
    // Query serves the stale cache from before sleep + the bell never
    // ticks until manual reload. The WS reconnect path in
    // `useGlobalRealtime` is the other half of the same defense.
    refetchOnWindowFocus: true,
    // Same logic for network reconnect — coming back online after a
    // network drop should refresh the bell.
    refetchOnReconnect: true,
    retry: false,
  })
}

/* -------------------------------------------------------------------------- */
/*                              useUnreadCount                                */
/* -------------------------------------------------------------------------- */

/**
 * Derive the unread count from the first page of `useNotifications`.
 * This is the simpler path called out in the Phase 3b plan — wherever
 * the bell mounts, `useNotifications()` already runs, so a separate
 * count endpoint would just duplicate the round-trip.
 */
export function useUnreadCount(): number {
  const query = useNotifications()
  const firstPage = query.data?.pages[0]
  return firstPage?.unreadCount ?? 0
}

/* -------------------------------------------------------------------------- */
/*                              useMarkRead                                   */
/* -------------------------------------------------------------------------- */

interface MarkReadArgs {
  id: string
}

interface MarkReadContext {
  previous: NotificationsCache | undefined
}

export function useMarkRead() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('notifications')

  return useMutation<void, Error, MarkReadArgs, MarkReadContext>({
    mutationFn: async ({ id }) => {
      await apiClient<void>(`/api/notifications/${id}/read`, { method: 'PATCH' })
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueryData<NotificationsCache>(NOTIFICATIONS_QUERY_KEY)

      // Was the target actually unread? Only decrement if so — clicking
      // an already-read row should be a no-op for the badge.
      const wasUnread = previous?.pages.some((page) =>
        page.items.some((n) => n.id === id && n.readAt === null),
      )

      queryClient.setQueryData<NotificationsCache>(NOTIFICATIONS_QUERY_KEY, (cache) =>
        mutateAllPages(
          cache,
          (item) =>
            item.id === id && item.readAt === null
              ? { ...item, readAt: new Date().toISOString() }
              : item,
          (current) => (wasUnread ? current - 1 : current),
        ),
      )

      return { previous }
    },
    onError: (_err, _args, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous)
      }
      toast.error(t('mutations.markReadError'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useMarkAllRead                                */
/* -------------------------------------------------------------------------- */

interface MarkAllReadContext {
  previous: NotificationsCache | undefined
}

export function useMarkAllRead() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useTranslation('notifications')

  return useMutation<void, Error, void, MarkAllReadContext>({
    mutationFn: async () => {
      await apiClient<void>('/api/notifications/read-all', { method: 'PATCH' })
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
      const previous = queryClient.getQueryData<NotificationsCache>(NOTIFICATIONS_QUERY_KEY)
      const now = new Date().toISOString()

      queryClient.setQueryData<NotificationsCache>(NOTIFICATIONS_QUERY_KEY, (cache) =>
        mutateAllPages(
          cache,
          (item) => (item.readAt === null ? { ...item, readAt: now } : item),
          () => 0,
        ),
      )

      return { previous }
    },
    onError: (_err, _args, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEY, context.previous)
      }
      toast.error(t('mutations.markAllReadError'))
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    },
  })
}
